import {inject, Injectable} from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {
  Era5DayNormalizedWeatherService,
  Era5DayWeatherForecastService,
  Era5DayWeatherReportService,
  Era5YearWeatherReportService
} from '@6grain/masofa-monolith-api-client/weather-report';
import {EraWeatherStationService} from '@6grain/masofa-monolith-api-client/weather';
import {FilterOperator} from '@app/utils/filter-operator';
import {
  deviationRange,
  keepStations,
  LegendValueRange,
  parseWeatherLayerPeriod,
  rangeOf,
  readReportNumber,
  reportFieldForLayer,
  StationPoint,
  stationsInsideArea,
  WeatherLayerPeriod,
  yearlyDeviationRange,
  yearlyWeatherRange
} from './legend-range.util';

type RangeDataType = 'weather' | 'normal' | 'deviation';
type RowsFilter = (rows: object[]) => object[];

/**
 * Min / max of the station values an ERA5 weather tile was painted from (docs/WEATHER.md, Legend).
 * Emits null whenever the range cannot be read so the caller keeps the catalog scale.
 */
@Injectable({providedIn: 'root'})
export class WeatherLegendRangeService {
  private readonly norms = inject(Era5DayNormalizedWeatherService);
  private readonly reports = inject(Era5DayWeatherReportService);
  private readonly forecasts = inject(Era5DayWeatherForecastService);
  private readonly yearReports = inject(Era5YearWeatherReportService);
  private readonly stationsApi = inject(EraWeatherStationService);

  private stations: StationPoint[] | null = null;

  /**
   * @param isInsideCountry point test against the country border drawn on the map; when absent,
   * every station in the response counts.
   */
  load(
    dataType: string,
    geoServerName: string,
    isInsideCountry?: (latitude: number, longitude: number) => boolean
  ): Observable<LegendValueRange | null> {
    if (!isRangeDataType(dataType) || !/^Era/i.test(geoServerName)) {
      return of(null);
    }

    const period = parseWeatherLayerPeriod(geoServerName);
    const field = reportFieldForLayer(geoServerName);
    if (!period || !field) {
      return of(null);
    }

    return this.rowsFilter(isInsideCountry).pipe(
      switchMap((keep) => this.loadRange(dataType, period, field, keep)),
      catchError(() => of(null))
    );
  }

  private loadRange(
    dataType: RangeDataType,
    period: WeatherLayerPeriod,
    field: string,
    keep: RowsFilter
  ): Observable<LegendValueRange | null> {
    switch (dataType) {
      case 'normal':
        return period.kind === 'norm-day'
          ? this.loadNorms(period.month, period.day).pipe(map((rows) => rangeOf(numericField(keep(rows), field))))
          : of(null);
      case 'weather':
        return this.loadWeatherRange(period, field, keep);
      case 'deviation':
        return this.loadDeviationRange(period, field, keep);
    }
  }

  private rowsFilter(
    isInsideCountry?: (latitude: number, longitude: number) => boolean
  ): Observable<RowsFilter> {
    if (!isInsideCountry) {
      return of((rows: object[]) => rows);
    }
    return this.loadStations().pipe(map((stations) => {
      const ids = stationsInsideArea(stations, isInsideCountry);
      return (rows: object[]) => keepStations(rows, ids);
    }));
  }

  private loadStations(): Observable<StationPoint[]> {
    if (this.stations) {
      return of(this.stations);
    }
    return this.stationsApi.weatherEraWeatherStationGetAllStationsGet().pipe(
      map((rows) => (rows ?? []).flatMap((row) =>
        row.id && row.latitude != null && row.longitude != null
          ? [{id: row.id, latitude: row.latitude, longitude: row.longitude}]
          : []
      )),
      tap((stations) => this.stations = stations)
    );
  }

  private loadWeatherRange(
    period: WeatherLayerPeriod,
    field: string,
    keep: RowsFilter
  ): Observable<LegendValueRange | null> {
    if (period.kind === 'yearly') {
      return this.loadYearReports(period.year).pipe(map((rows) => yearlyWeatherRange(keep(rows), field)));
    }
    if (period.kind !== 'daily') {
      return of(null);
    }
    return this.loadDayRows(period).pipe(
      switchMap((reports) => {
        const fromReports = rangeOf(numericField(keep(reports), field));
        return fromReports
          ? of(fromReports)
          : this.loadForecastRows(period).pipe(map((rows) => rangeOf(numericField(keep(rows), field))));
      })
    );
  }

  private loadDeviationRange(
    period: WeatherLayerPeriod,
    field: string,
    keep: RowsFilter
  ): Observable<LegendValueRange | null> {
    if (period.kind === 'yearly') {
      return forkJoin({
        reports: this.loadYearReports(period.year),
        norms: this.loadAllNorms()
      }).pipe(map(({reports, norms}) => yearlyDeviationRange(keep(reports), norms, field)));
    }
    if (period.kind !== 'daily') {
      return of(null);
    }
    const daily = period;
    return this.loadNorms(daily.month, daily.day).pipe(
      switchMap((norms) => this.loadDayRows(daily).pipe(
        switchMap((reports) => {
          const fromReports = deviationRange(keep(reports), norms, field);
          return fromReports
            ? of(fromReports)
            : this.loadForecastRows(daily).pipe(map((rows) => deviationRange(keep(rows), norms, field)));
        })
      ))
    );
  }

  private loadNorms(month: number, day: number): Observable<object[]> {
    return this.norms.weatherReportsEra5DayNormalizedWeatherGetByQueryPost({
      filters: [
        {filterField: 'month', filterValue: month, filterOperator: FilterOperator.Equals},
        {filterField: 'day', filterValue: day, filterOperator: FilterOperator.Equals}
      ]
    }).pipe(map((rows) => rows ?? []));
  }

  private loadAllNorms(): Observable<object[]> {
    return this.norms.weatherReportsEra5DayNormalizedWeatherGetByQueryPost({filters: []})
      .pipe(map((rows) => rows ?? []));
  }

  private loadYearReports(year: number): Observable<object[]> {
    return this.yearReports.weatherReportsEra5YearWeatherReportGetByQueryPost({
      filters: [{filterField: 'year', filterValue: year, filterOperator: FilterOperator.Equals}]
    }).pipe(map((rows) => rows ?? []));
  }

  private loadDayRows(period: {year: number; month: number; day: number}): Observable<object[]> {
    return this.reports.weatherReportsEra5DayWeatherReportGetByQueryPost({filters: dateFilter(period)})
      .pipe(map((rows) => rows ?? []));
  }

  private loadForecastRows(period: {year: number; month: number; day: number}): Observable<object[]> {
    return this.forecasts.weatherReportsEra5DayWeatherForecastGetByQueryPost({filters: dateFilter(period)})
      .pipe(map((rows) => rows ?? []));
  }
}

function isRangeDataType(value: string): value is RangeDataType {
  return value === 'weather' || value === 'normal' || value === 'deviation';
}

function dateFilter(period: {year: number; month: number; day: number}) {
  const date = `${period.year}-${pad(period.month)}-${pad(period.day)}`;
  return [{filterField: 'date', filterValue: date, filterOperator: FilterOperator.Equals}];
}

function numericField(rows: object[], field: string): number[] {
  return rows
    .map((row) => readReportNumber(row, field))
    .filter((value): value is number => value != null);
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}
